import React, { useState, useEffect, useRef } from "react";

// ─── CYBERPUNK THEME ────────────────────────────────────────────────
const theme = {
  bg: "#0a0a0f",
  panel: "#0d0d14",
  panelBorder: "#1a1a2e",
  neon: "#00f0ff",
  neonDim: "#00f0ff33",
  neonGlow: "0 0 20px #00f0ff44, 0 0 40px #00f0ff22",
  profit: "#00ff88",
  profitDim: "#00ff8833",
  loss: "#ff3366",
  lossDim: "#ff336633",
  warn: "#ffaa00",
  text: "#e0e0f0",
  textDim: "#606080",
  textMid: "#9090b0",
  accent: "#7b2fff",
  accentDim: "#7b2fff44",
  gridLine: "#1a1a2e",
};

const Scanlines = () => (
  <div style={{
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none", zIndex: 9999,
    background: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px)",
  }} />
);

const GlitchText = ({ children, style = {} }) => {
  const [glitch, setGlitch] = useState(false);
  useEffect(() => {
    const iv = setInterval(() => {
      setGlitch(true);
      setTimeout(() => setGlitch(false), 100 + Math.random() * 100);
    }, 3000 + Math.random() * 5000);
    return () => clearInterval(iv);
  }, []);
  return (
    <span style={{ position: "relative", display: "inline-block", ...style }}>
      {children}
      {glitch && (
        <>
          <span style={{ position: "absolute", left: -2, top: 0, color: theme.loss, clipPath: "inset(20% 0 50% 0)", opacity: 0.7 }}>{children}</span>
          <span style={{ position: "absolute", left: 2, top: 0, color: theme.neon, clipPath: "inset(50% 0 20% 0)", opacity: 0.7 }}>{children}</span>
        </>
      )}
    </span>
  );
};

// ─── BOT DEFINITIONS ────────────────────────────────────────────────
const BOTS = [
  {
    id: "boundary",
    name: "BOUNDARY INVERSION",
    subtitle: "Polymarket 5-Min Streak Reversal",
    href: "/boundary/",
    wsPath: "/ws/boundary",
    accent: "#bf5af2",
    icon: "\u21C4",
    description: "Streak reversal on 5-min BTC/SOL markets. HybridSizer: KellyReset (BTC) + TrailingStop (SOL). Shadow paper-trading old FibMart for comparison.",
    parseSnapshot: (msg) => {
      if (msg.type !== "snapshot") return null;
      const trades = msg.trades || [];
      const resolved = trades.filter(t => t.result !== "PENDING");
      const wins = resolved.filter(t => t.result === "WIN").length;
      const totalPnl = resolved.reduce((s, t) => s + parseFloat(t.pnl || 0), 0);
      const pending = trades.filter(t => t.result === "PENDING").length;
      const shadowPnl = msg.shadow_pnl_total || 0;
      return {
        balance: msg.balance || 0,
        totalPnl,
        winRate: resolved.length > 0 ? (wins / resolved.length * 100) : 0,
        totalTrades: resolved.length,
        wins,
        losses: resolved.length - wins,
        pending,
        shadowPnl,
      };
    },
  },
  {
    id: "weather",
    name: "WEATHER ARB",
    subtitle: "Polymarket Temperature Markets",
    href: "/weather/",
    wsPath: "/ws/weather",
    accent: "#ff6b35",
    icon: "\u2600",
    description: "Temperature bucket arbitrage across 10 global cities. Multi-model forecasting with bias correction and tiered position sizing.",
    parseSnapshot: (msg) => {
      if (msg.type !== "snapshot") return null;
      return {
        balance: msg.balance || 0,
        totalPnl: msg.total_pnl || 0,
        winRate: (() => {
          const trades = msg.trades || [];
          const resolved = trades.filter(t => t.status === "resolved" || t.status === "exited");
          const wins = resolved.filter(t => parseFloat(t.pnl) > 0).length;
          return resolved.length > 0 ? (wins / resolved.length * 100) : 0;
        })(),
        totalTrades: (msg.trades || []).filter(t => t.status === "resolved" || t.status === "exited").length,
        wins: (msg.trades || []).filter(t => (t.status === "resolved" || t.status === "exited") && parseFloat(t.pnl) > 0).length,
        losses: (msg.trades || []).filter(t => (t.status === "resolved" || t.status === "exited") && parseFloat(t.pnl) <= 0).length,
        pending: (msg.status_counts || {}).filled || 0,
      };
    },
  },
  {
    id: "nhl",
    name: "NHL HOCKEY",
    subtitle: "Polymarket NHL O/U Totals",
    href: "/nhl/",
    wsPath: null,
    accent: "#10b981",
    icon: "\uD83C\uDFD2",
    description: "Goalie-specific over/under prediction model. Live NHL API data, ESPN confirmed starters, individual SV% adjustments. Scaled Kelly sizing.",
    staticDataUrl: "/nhl/positions.json",
    parseSnapshot: () => null,
  },
];

// ─── BOT CARD ───────────────────────────────────────────────────────
const BotCard = ({ bot }) => {
  const [status, setStatus] = useState("offline");
  const [stats, setStats] = useState(null);
  const wsRef = useRef(null);

  useEffect(() => {
    // Static data bots (no WebSocket)
    if (bot.staticDataUrl) {
      setStatus("connecting");
      fetch(bot.staticDataUrl + "?t=" + Date.now())
        .then(r => r.json())
        .then(data => {
          const positions = data.positions || [];
          const history = data.history || [];
          const all = [...positions, ...history];
          const resolved = all.filter(p => p.status === "resolved");
          const wins = resolved.filter(p => p.result === "win").length;
          const openCost = positions.filter(p => p.status === "open").reduce((s, p) => s + p.cost, 0);
          const resolvedPnl = resolved.reduce((s, p) => s + (p.pnl || 0), 0);
          setStats({
            balance: data.bankroll || 0,
            totalPnl: resolvedPnl,
            winRate: resolved.length > 0 ? (wins / resolved.length * 100) : 0,
            totalTrades: resolved.length,
            wins,
            losses: resolved.length - wins,
            pending: positions.filter(p => p.status === "open").length,
            openRisk: openCost,
          });
          setStatus("live");
        })
        .catch(() => setStatus("offline"));
      return;
    }

    // WebSocket bots
    let reconnectTimer;
    const connect = () => {
      setStatus("connecting");
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const ws = new WebSocket(`${protocol}//${window.location.host}${bot.wsPath}`);
      wsRef.current = ws;
      ws.onopen = () => setStatus("live");
      ws.onclose = () => { setStatus("offline"); reconnectTimer = setTimeout(connect, 5000); };
      ws.onerror = () => ws.close();
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          const parsed = bot.parseSnapshot(msg);
          if (parsed) setStats(parsed);
        } catch (e) { console.error("WS parse error", e); }
      };
    };
    connect();
    return () => { clearTimeout(reconnectTimer); if (wsRef.current) wsRef.current.close(); };
  }, [bot]);

  const statusColor = status === "live" ? theme.profit : status === "connecting" ? theme.warn : theme.loss;
  const pnl = stats?.totalPnl || 0;
  const wr = stats?.winRate || 0;

  return (
    <a href={bot.href} style={{ textDecoration: "none", color: "inherit" }}>
      <div style={{
        background: `linear-gradient(135deg, ${theme.panel} 0%, #0b0b12 100%)`,
        border: `1px solid ${theme.panelBorder}`,
        borderRadius: 6,
        padding: "28px 32px",
        position: "relative",
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 0.3s ease",
        minHeight: 320,
      }}
        onMouseOver={e => { e.currentTarget.style.borderColor = bot.accent; e.currentTarget.style.boxShadow = `0 0 30px ${bot.accent}22`; }}
        onMouseOut={e => { e.currentTarget.style.borderColor = theme.panelBorder; e.currentTarget.style.boxShadow = "none"; }}
      >
        {/* Top glow line */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${bot.accent}, transparent)` }} />

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 28, marginBottom: 8 }}>{bot.icon}</div>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 18, fontWeight: 900, color: bot.accent, letterSpacing: 3, textShadow: `0 0 20px ${bot.accent}44` }}>{bot.name}</div>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, color: theme.textDim, marginTop: 4 }}>{bot.subtitle}</div>
          </div>
          <div style={{
            fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 2, padding: "4px 12px", borderRadius: 2,
            background: statusColor + "22", color: statusColor,
            border: `1px solid ${statusColor}44`,
            animation: status === "live" ? "pulse 2s infinite" : "none",
          }}>{"\u25CF"} {status.toUpperCase()}</div>
        </div>

        {/* Description */}
        <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 11, color: theme.textMid, lineHeight: 1.6, marginBottom: 20, maxWidth: 500 }}>
          {bot.description}
        </div>

        {/* Stats */}
        {stats ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
            <div style={{ background: "#08080e", border: `1px solid ${theme.panelBorder}`, borderRadius: 3, padding: "10px 12px" }}>
              <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1, color: theme.textDim, marginBottom: 4 }}>TRADING P&L</div>
              <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 16, color: pnl >= 0 ? theme.profit : theme.loss }}>{pnl >= 0 ? "+" : ""}{pnl.toFixed(2)}</div>
            </div>
            <div style={{ background: "#08080e", border: `1px solid ${theme.panelBorder}`, borderRadius: 3, padding: "10px 12px" }}>
              <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1, color: theme.textDim, marginBottom: 4 }}>WIN RATE</div>
              <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 16, color: stats.totalTrades > 0 ? (wr >= 60 ? theme.profit : wr >= 40 ? theme.warn : theme.loss) : theme.textMid }}>{stats.totalTrades > 0 ? wr.toFixed(1) + "%" : "—"}</div>
            </div>
            <div style={{ background: "#08080e", border: `1px solid ${theme.panelBorder}`, borderRadius: 3, padding: "10px 12px" }}>
              <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1, color: theme.textDim, marginBottom: 4 }}>{stats.openRisk !== undefined ? "OPEN" : "TRADES"}</div>
              <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 16, color: theme.text }}>
                {stats.openRisk !== undefined ? (
                  <><span style={{ color: theme.warn }}>${stats.openRisk.toFixed(0)}</span><span style={{ fontSize: 10, color: theme.neon, marginLeft: 6 }}>{stats.pending} pos</span></>
                ) : (
                  <>{stats.totalTrades}{stats.pending > 0 && <span style={{ fontSize: 10, color: theme.warn, marginLeft: 6 }}>+{stats.pending}</span>}</>
                )}
              </div>
            </div>
            <div style={{ background: "#08080e", border: `1px solid ${theme.panelBorder}`, borderRadius: 3, padding: "10px 12px" }}>
              <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1, color: theme.textDim, marginBottom: 4 }}>BANKROLL</div>
              <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 16, color: theme.neon }}>${stats.balance.toFixed(0)}</div>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: theme.textDim, fontFamily: "'Share Tech Mono', monospace", fontSize: 12 }}>
            {status === "connecting" ? "Connecting..." : status === "offline" ? "Bot offline" : "Loading..."}
          </div>
        )}

        {/* Shadow P&L for boundary bot */}
        {bot.id === "boundary" && stats?.shadowPnl !== undefined && stats.totalTrades > 0 && (
          <div style={{ marginTop: 12, background: "#08080e", border: `1px solid ${theme.panelBorder}`, borderRadius: 3, padding: "8px 12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 1, color: theme.textDim }}>PAPER TRADE (OLD FIBMART)</div>
            <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 13, color: theme.textMid }}>
              ${stats.shadowPnl >= 0 ? "+" : ""}{stats.shadowPnl.toFixed(2)}
              <span style={{ fontSize: 10, color: pnl > stats.shadowPnl ? theme.profit : theme.loss, marginLeft: 8 }}>
                {pnl > stats.shadowPnl ? "LIVE AHEAD" : "SHADOW AHEAD"} by ${Math.abs(pnl - stats.shadowPnl).toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {/* Bottom accent bar */}
        <div style={{ position: "absolute", bottom: 0, left: "10%", right: "10%", height: 1, background: `linear-gradient(90deg, transparent, ${bot.accent}33, transparent)` }} />
      </div>
    </a>
  );
};

// ─── AGGREGATE STATS ────────────────────────────────────────────────
const AggregateBar = ({ bots, botStats }) => {
  let totalPnl = 0, totalTrades = 0, totalWins = 0, liveBots = 0, totalBalance = 0;
  for (const b of bots) {
    const s = botStats[b.id];
    if (s) {
      totalPnl += s.totalPnl;
      totalTrades += s.totalTrades;
      totalWins += s.wins;
      totalBalance += s.balance;
      liveBots++;
    }
  }
  const wr = totalTrades > 0 ? (totalWins / totalTrades * 100) : 0;

  return (
    <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
      <div style={{ flex: 1, background: "#08080e", border: `1px solid ${theme.panelBorder}`, borderRadius: 3, padding: "12px 16px" }}>
        <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 2, color: theme.textDim, marginBottom: 6 }}>COMBINED BALANCE</div>
        <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 24, color: theme.neon }}>${totalBalance.toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
      </div>
      <div style={{ flex: 1, background: "#08080e", border: `1px solid ${theme.panelBorder}`, borderRadius: 3, padding: "12px 16px" }}>
        <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 2, color: theme.textDim, marginBottom: 6 }}>TOTAL P&L</div>
        <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 24, color: totalPnl >= 0 ? theme.profit : theme.loss }}>{totalPnl >= 0 ? "+" : ""}{totalPnl.toFixed(2)}</div>
      </div>
      <div style={{ flex: 1, background: "#08080e", border: `1px solid ${theme.panelBorder}`, borderRadius: 3, padding: "12px 16px" }}>
        <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 2, color: theme.textDim, marginBottom: 6 }}>COMBINED WIN RATE</div>
        <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 24, color: wr >= 60 ? theme.profit : wr >= 40 ? theme.warn : theme.loss }}>{wr.toFixed(1)}%</div>
      </div>
      <div style={{ flex: 1, background: "#08080e", border: `1px solid ${theme.panelBorder}`, borderRadius: 3, padding: "12px 16px" }}>
        <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 2, color: theme.textDim, marginBottom: 6 }}>TOTAL TRADES</div>
        <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 24, color: theme.text }}>{totalTrades}</div>
      </div>
      <div style={{ flex: 1, background: "#08080e", border: `1px solid ${theme.panelBorder}`, borderRadius: 3, padding: "12px 16px" }}>
        <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 2, color: theme.textDim, marginBottom: 6 }}>ACTIVE BOTS</div>
        <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 24, color: liveBots > 0 ? theme.profit : theme.loss }}>{liveBots} / {bots.length}</div>
      </div>
    </div>
  );
};

// ─── MAIN APP ───────────────────────────────────────────────────────
function App() {
  const [clock, setClock] = useState(new Date());
  const [botStats, setBotStats] = useState({});

  useEffect(() => { const iv = setInterval(() => setClock(new Date()), 1000); return () => clearInterval(iv); }, []);

  // Connect to each bot's WebSocket (or fetch static data) for aggregate stats
  useEffect(() => {
    const connections = [];
    for (const bot of BOTS) {
      // Static data bots
      if (bot.staticDataUrl) {
        fetch(bot.staticDataUrl + "?t=" + Date.now())
          .then(r => r.json())
          .then(data => {
            const positions = data.positions || [];
            const history = data.history || [];
            const all = [...positions, ...history];
            const resolved = all.filter(p => p.status === "resolved");
            const wins = resolved.filter(p => p.result === "win").length;
            const resolvedPnl = resolved.reduce((s, p) => s + (p.pnl || 0), 0);
            setBotStats(prev => ({ ...prev, [bot.id]: {
              balance: data.bankroll || 0,
              totalPnl: resolvedPnl,
              winRate: resolved.length > 0 ? (wins / resolved.length * 100) : 0,
              totalTrades: resolved.length,
              wins,
              losses: resolved.length - wins,
              pending: positions.filter(p => p.status === "open").length,
            }}));
          })
          .catch(() => {});
        continue;
      }

      // WebSocket bots
      let reconnectTimer;
      const connect = () => {
        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        const ws = new WebSocket(`${protocol}//${window.location.host}${bot.wsPath}`);
        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            const parsed = bot.parseSnapshot(msg);
            if (parsed) setBotStats(prev => ({ ...prev, [bot.id]: parsed }));
          } catch (e) { /* ignore */ }
        };
        ws.onclose = () => { reconnectTimer = setTimeout(connect, 5000); };
        ws.onerror = () => ws.close();
        connections.push({ ws, timer: () => clearTimeout(reconnectTimer) });
      };
      connect();
    }
    return () => { connections.forEach(c => { c.timer(); c.ws.close(); }); };
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: `radial-gradient(ellipse at 50% 30%, #0f0a1a 0%, ${theme.bg} 70%)`, color: theme.text, padding: "32px 40px", fontFamily: "'Share Tech Mono', monospace" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Share+Tech+Mono&display=swap');
        @keyframes fadeIn { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
        * { box-sizing: border-box; }
        body { margin: 0; background: ${theme.bg}; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: ${theme.bg}; }
        ::-webkit-scrollbar-thumb { background: ${theme.accent}44; border-radius: 2px; }
      `}</style>
      <Scanlines />

      {/* HEADER */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32 }}>
        <div>
          <GlitchText style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 28, fontWeight: 900, color: theme.neon, textShadow: theme.neonGlow, letterSpacing: 6 }}>
            COMMAND CENTER
          </GlitchText>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 3, color: theme.textDim, marginTop: 8 }}>POLYMARKET TRADING OPERATIONS</div>
        </div>
        <div style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 14, color: theme.textMid }}>{clock.toUTCString().replace("GMT", "UTC")}</div>
      </div>

      {/* AGGREGATE STATS */}
      <AggregateBar bots={BOTS} botStats={botStats} />

      {/* BOT CARDS */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
        {BOTS.map(bot => <BotCard key={bot.id} bot={bot} />)}
      </div>

      <div style={{ textAlign: "center", padding: "24px 0 8px", fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 3, color: theme.textDim }}>
        COMMAND CENTER v1.1 &mdash; <a href="https://polymarket.com/?via=LFo4Mk3" target="_blank" rel="noopener noreferrer" style={{ color: theme.neon, textDecoration: "none" }}>TRADE ON POLYMARKET</a>
      </div>
    </div>
  );
}

export default App;
